// src/Routes.tsx
import { UserInfoUserRole } from "@/lib/api/interfaces/user-profile.interface";
import { ProtectedRoute } from "@/util-components/root/protected-route";
import * as Sentry from "@sentry/react";
import { Navigate, useParams, useRoutes } from "react-router-dom";
import CustomerHome from "./page-components/home/customer/page";
import InternalHome from "./page-components/home/internal/page";
import IntegrationsCallback from "./page-components/integrations/callback/page";
import Integrations from "./page-components/integrations/page";
import MatchesWrapper, { MatchesList } from "./page-components/matches/page";
import OpponentsPage from "./page-components/opponents/page";
import PlayersWrapper from "./page-components/players/page";
import PostGenerator from "./page-components/post-generator/page";
import ChoosePlanPage from "./page-components/choose-plan/page";
import BillingSuccessPage from "./page-components/billing/success/page";
import Logout from "./page-components/register/logout/page";
import Register from "./page-components/register/page";
import ResetPassword from "./page-components/register/reset-password/page";
import ChangelogPage from "./page-components/changelog/page";
import SettingsDashboard, { SettingsAppPage, SettingsProfilePage, SettingsSecurityPage } from "./page-components/settings/page";
import TeamsWrapper from "./page-components/teams/page";
import CustomerUsersInvitationsWrapper from "./page-components/users/customer/invitations/page";
import CustomerUsersWrapper from "./page-components/users/customer/page";
import InternalUsersInvitationsWrapper from "./page-components/users/internal/invitations/page";
import InternalUsersWrapper from "./page-components/users/internal/page";
import CreateTeam from "./page-components/vereine/customer/create/page";
import OrganizationDashboard from "./page-components/vereine/customer/page";
import { OrganizationBranding } from "./page-components/vereine/customer/branding";
import TeamDetails from "./page-components/vereine/internal/[id]/page";
import InternalTeamsWrapper from "./page-components/vereine/internal/page";
import MailVerification from "./page-components/verification/page";
import PageNotFound from "./util-components/error/page-not-found";
import InternalCustomerWrapper from "./util-components/root/internal-customer-wrapper";
import MediaLibrary from "./page-components/post-generator/media-library/page";
import Templates from "./page-components/post-generator/templates/page";
import PlannedPosts from "./page-components/post-generator/planned-posts/page";
import PastPosts from "./page-components/post-generator/past-posts/page";
import TemplateEditor from "./page-components/post-generator/template-editor/page";
import CreatePost from "./page-components/post-generator/create-post/CreatePostPage";
import SponsorsV2Wrapper from "@/page-components/sponsors/page";
import Sponsors from "@/page-components/sponsors/sponsors/page";
import { Discover } from "@/page-components/sponsors/sponsors/discover";
import SponsorDetailsPage from "@/page-components/sponsors/sponsors/SponsorDetailsPage";
import StoreTemplates from "./page-components/post-generator/store-templates/page";
import DefaultTemplatesPage from "./page-components/post-generator/default-templates/DefaultTemplatesPage";
import AdvertisementsWrapper from "./page-components/sponsors/advertisements/page";
import {OrganizationAdvancedFeatures} from "@/page-components/vereine/customer/advanced-features";
import { PostGeneratorStartPage } from "./page-components/post-generator/start/page";
import CompleteProfile from "./page-components/complete-profile/page";
import OrganizationBillingPage from "./page-components/vereine/customer/billing/page";
import BannedPage from "./page-components/banned/page";
import UserNotFoundPage from "./page-components/user-not-found/page";
import AdminPage from "./page-components/admin/page";
import { OrganizationAiCaptionSettings } from "./page-components/vereine/customer/ai-caption-settings";

/**
 * Main Routing Component.
 * 
 * Defines all application routes and their access protections.
 * Uses `ProtectedRoute` to enforce role-based access.
 * Uses `InternalCustomerWrapper` to split logic for different user types on the same route.
 */
const useSentryRoutes = Sentry.wrapUseRoutesV6(useRoutes);

const LegacySponsorDetailRedirect = () => {
  const { id } = useParams();
  return <Navigate to={id ? `/sponsoring/sponsoren/${id}` : "/sponsoring/sponsoren"} replace />;
};

const Routes = () => {
  const routes = useSentryRoutes([
    // Public routes (no authentication required)
    {
      path: "/register",
      element: <Register />,
    },
    {
      path: "/reset",
      element: <ResetPassword />,
    },
    {
      path: "/verification/mail",
      element: <MailVerification />,
    },
    {
      path: "/logout",
      element: <Logout />,
    },
    {
      path: "/banned",
      element: <BannedPage />,
    },
    {
      path: "/user-not-found",
      element: <UserNotFoundPage />,
    },
    {
      path: "/complete-profile",
      element: <CompleteProfile />,
    },

    // Protected routes (require authentication + role-based access)
    {
      path: "/changelog",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <InternalCustomerWrapper customerComponent={<ChangelogPage />} internalComponent={<ChangelogPage />} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/choose-plan",
      element: <ChoosePlanPage />,
    },
    {
      path: "/billing/success",
      element: <BillingSuccessPage />,
    },
    {
      path: "/",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <InternalCustomerWrapper customerComponent={<CustomerHome />} internalComponent={<InternalHome />} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/settings",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <SettingsDashboard />
        </ProtectedRoute>
      ),
    },
    {
      path: "/settings/profil",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <SettingsProfilePage />
        </ProtectedRoute>
      ),
    },
    {
      path: "/settings/sicherheit",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <SettingsSecurityPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "/settings/app",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <SettingsAppPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "/admin",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.ADMIN} disablePadding>
          <InternalCustomerWrapper internalComponent={<AdminPage />} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/mannschaften",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <InternalCustomerWrapper customerComponent={<TeamsWrapper />} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/vereine",
      element: (
        <ProtectedRoute minRoleMap={{ internal: UserInfoUserRole.ADMIN, customer: UserInfoUserRole.USER }} disablePadding>
          <InternalCustomerWrapper
            internalComponent={<InternalTeamsWrapper />}
            customerComponent={
              <OrganizationDashboard />
            }
          />
        </ProtectedRoute>
      ),
    },
    {
      path: "/vereine/branding",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
           <OrganizationBranding />
        </ProtectedRoute>
      ),
    },
    {
      path: "/vereine/advanced-features",
      element: (
          <ProtectedRoute minRole={UserInfoUserRole.ADMIN} disablePadding>
            <OrganizationAdvancedFeatures />
          </ProtectedRoute>
      ),
    },
    {
      path: "/vereine/billing",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.ADMIN} disablePadding>
          <OrganizationBillingPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "/vereine/ki-anpassung",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.ADMIN} disablePadding>
          <OrganizationAiCaptionSettings />
        </ProtectedRoute>
      ),
    },
    {
      path: "/vereine/:id",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.ADMIN}>
          <InternalCustomerWrapper internalComponent={<TeamDetails />} />
        </ProtectedRoute>
      ),
    },

    {
      // only for external users
      path: "/vereine/create",
      element: <CreateTeam />,
    },
    {
      path: "/users",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.ADMIN} disablePadding>
          <InternalCustomerWrapper
            internalComponent={<InternalUsersWrapper />}
            customerComponent={
              <CustomerUsersWrapper />
            }
          />
        </ProtectedRoute>
      ),
    },
    {
      path: "/users/invitations",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.ADMIN} disablePadding>
          <InternalCustomerWrapper
            internalComponent={<InternalUsersInvitationsWrapper />}
            customerComponent={
              <CustomerUsersInvitationsWrapper />
            }
          />
        </ProtectedRoute>
      ),
    },
    {
      path: "/spieler",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <InternalCustomerWrapper customerComponent={<PlayersWrapper />} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/sponsoring",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <InternalCustomerWrapper customerComponent={<SponsorsV2Wrapper />} />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <Navigate to="sponsoren" replace />,
        },
        {
          path: "sponsoren",
          element: <Sponsors />,
        },
        {
          path: "leistungen",
          element: <AdvertisementsWrapper />,
        },
        {
          path: "packages",
          element: <MediaLibrary />,
        },
        {
          path: "discover",
          element: <Discover />,
        },
        {
          path: "sponsors",
          element: <Navigate to="/sponsoring/sponsoren" replace />,
        },
        {
          path: "add-banners",
          element: <Navigate to="/sponsoring/leistungen" replace />,
        },
      ],
    },
    {
      path: "/sponsoring/sponsoren/:id",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <InternalCustomerWrapper customerComponent={<SponsorDetailsPage />} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/sponsors/sponsors/:id",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <LegacySponsorDetailRedirect />
        </ProtectedRoute>
      ),
    },
    {
      path: "/sponsors/sponsors",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <Navigate to="/sponsoring/sponsoren" replace />
        </ProtectedRoute>
      ),
    },
    {
      path: "/sponsors/add-banners",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <Navigate to="/sponsoring/leistungen" replace />
        </ProtectedRoute>
      ),
    },
    {
      path: "/sponsors/*",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <Navigate to="/sponsoring/sponsoren" replace />
        </ProtectedRoute>
      ),
    },
    {
      path: "/spiele",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <InternalCustomerWrapper customerComponent={<MatchesWrapper />} />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <MatchesList />,
        },
        {
          path: "matches",
          element: <Navigate to="/spiele" replace />,
        },
      ],
    },
    {
      path: "/gegner",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <InternalCustomerWrapper customerComponent={<OpponentsPage />} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/opponents",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <Navigate to="/gegner" replace />
        </ProtectedRoute>
      ),
    },
    {
      path: "/post-generator",
      element: (
        <ProtectedRoute minRoleMap={{internal: UserInfoUserRole.USER, customer: UserInfoUserRole.USER}} disablePadding>
          <PostGenerator />
        </ProtectedRoute>
      ),
      children: [
        {
          index: true,
          element: <Navigate to="start" replace />,
        },
        {
          path: "start",
          element: <PostGeneratorStartPage />,
        },
        {
          path: "media-library",
          element: <MediaLibrary />,
        },
        {
          path: "store",
          element: <StoreTemplates />,
        },
      ]
    },
    {
      path: "/post-generator/planned",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <InternalCustomerWrapper customerComponent={<PlannedPosts />} internalComponent={<PlannedPosts />} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/post-generator/published",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <InternalCustomerWrapper customerComponent={<PastPosts />} internalComponent={<PastPosts />} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/post-generator/templates",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <InternalCustomerWrapper customerComponent={<Templates />} internalComponent={<Templates />} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/post-generator/default-templates",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <InternalCustomerWrapper customerComponent={<DefaultTemplatesPage />} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/post-generator/default-templates/:highlightType",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <InternalCustomerWrapper customerComponent={<DefaultTemplatesPage />} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/post-generator/template-editor",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <InternalCustomerWrapper customerComponent={<TemplateEditor />} internalComponent={<TemplateEditor />} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/post-generator/create-post",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER} disablePadding>
          <InternalCustomerWrapper customerComponent={<CreatePost />} internalComponent={<CreatePost />} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/integrations",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.ADMIN} disablePadding>
          <InternalCustomerWrapper
            customerComponent={
              <Integrations />
            }
          />
        </ProtectedRoute>
      ),
    },
    {
      path: "/meta/callback",
      element: (
        <ProtectedRoute minRole={UserInfoUserRole.USER}>
          <InternalCustomerWrapper customerComponent={<IntegrationsCallback name="Meta" />} />
        </ProtectedRoute>
      ),
    },
    {
      path: "/sign-out",
      element: <Logout />,
    },
    {
      path: "/*",
      element: <PageNotFound />,
    },
  ]);

  return routes;
};

export default Routes;
